import React from 'react';
import '../publishing-dialog.css';
import { useNavigate, useParams } from 'react-router-dom';
import { resolveResourceNameFromType } from '../utils';
import { formatCurrencyToDefault, formatCurrencyUnitPrice } from '../../../localization';
import { EditorService } from '../../../services/editor';

interface IPublishingPriceForecastProps {
	details: any;
	paymentMethods: any[];
	dataIsLoading: boolean;
	project: EditorService
}

function resolveResourceNameFromCostName(costName: string) {
	switch (costName) {
		case 'first-published-project-monthly-discount':
			return 'First project discount';
		default:
			return resolveResourceNameFromType(costName as any);
	}
}

const PublishingPriceForecast: React.FC<IPublishingPriceForecastProps> = ({
	details,
	paymentMethods,
	dataIsLoading,
	project
}) => {
	const { id: projectId } = useParams<{ id: string }>();
	const navigate = useNavigate();

	// Guard against accessing monthlyCostForecast if details are not yet loaded
	const hasFirstProjectDiscount = details?.monthlyCostForecast?.items?.find(
		(item: any) => item.name === 'first-published-project-monthly-discount'
	);

	const hasAtLeastOnePaymentMethod = paymentMethods.length > 0;

	return (
		<div className='forecast-pricing'>
			{/* Left block: Cost Forecast List */}
			<div className='forecast-pricing--left-col'>
				<h3 className='forecast-pricing--heading'>Cost forecast</h3>

				<ul className='forecast-pricing--list'>
					{details?.monthlyCostForecast?.items.map((item: any, i: number) => {
						const isDiscount = item.type === 'discount';

						return (
							<li
								key={item.name + '-' + i}
								className={`forecast-pricing--list-item ${
									isDiscount ? 'is-discount' : ''
								}`}
							>
								<div className='forecast-pricing--item-meta'>
									<div className='forecast-pricing--item-name'>
										{resolveResourceNameFromCostName(item.name)}
									</div>
									
									{/* Sub Groups (e.g. "Small (0.25 vCPU, 0.5GB RAM)") */}
									{item.subGroups && item.subGroups.map((subGroup: any, j: number) => (
										<div key={j} style={{ marginLeft: 16, marginTop: 8 }}>
											<div className='forecast-pricing--item-name' style={{ fontSize: '13px' }}>
												{subGroup.amount} unit{subGroup.amount !== 1 ? 's' : ''} {subGroup.name}
											</div>
											{subGroup.subItems?.map((subItem: any, k: number) => (
												<div key={k} className='forecast-pricing--item-subtext' style={{ marginLeft: 12 }}>
													{subItem.amount} {subItem.unitLabel}{subItem.amount !== 1 ? 's' : ''} at {formatCurrencyUnitPrice(subItem.value)} / {subItem.unitLabel}
												</div>
											))}
										</div>
									))}
									
									{/* Sub Items directly under the group (e.g. Relational Databases) */}
									{item.subItems && !item.subGroups && item.subItems.map((subItem: any, k: number) => (
										<div key={k} className='forecast-pricing--item-subtext' style={{ marginLeft: 16, marginTop: 4 }}>
											{subItem.amount} {subItem.unitLabel}{subItem.amount !== 1 ? 's' : ''} at {formatCurrencyUnitPrice(subItem.value)} / {subItem.unitLabel}
										</div>
									))}

									{/* Legacy / Simple format if no subgroups/subitems exist */}
									{!isDiscount && !item.subGroups && !item.subItems && (
										<div className='forecast-pricing--item-subtext'>
											{item.amount} unit{item.amount !== 1 ? 's' : ''} at{' '}
											{formatCurrencyUnitPrice(item.value)} / unit
										</div>
									)}
								</div>
								<div className='forecast-pricing--item-price' style={{ marginTop: 2 }}>
									{isDiscount ? '- ' : ''}
									{formatCurrencyToDefault(item.value)}
								</div>
							</li>
						);
					})}

					<hr className='forecast-pricing--divider' />

					{/* Total */}
					<li className='forecast-pricing--total-row'>
						<div>Project total monthly forecast</div>
						<div>
							{formatCurrencyToDefault(
								details?.monthlyCostForecast?.total?.value || 0
							)}
						</div>
					</li>
					<p className='forecast-pricing--tax-label'>Including Tax</p>
				</ul>
			</div>

			{/* Right block: Information & Loading */}
			<div className='forecast-pricing--right-col'>
				{dataIsLoading ? (
					<div className='forecast-pricing--loading-container'>
						<div
							className='forecast-pricing--loading-bar'
							style={{ width: '80%' }}
						/>
						<div
							className='forecast-pricing--loading-bar'
							style={{ width: '90%' }}
						/>
						<div
							className='forecast-pricing--loading-bar'
							style={{ width: '70%' }}
						/>
					</div>
				) : (
					<>
						<p className='forecast-pricing--disclaimer'>
							This is only a <u>forecast</u> based on your apps usage.
							<br />
							You are charged at the end of the month for what you used.
						</p>
						<p className='forecast-pricing--disclaimer'>
							Your total Elyx cost might be higher based on your other projects
							and costs.
						</p>
						<p className='forecast-pricing--disclaimer'>
							*This is a new project, so the forecast assumes near-zero traffic.
							<br />
							You can monitor your project's real-time cost on the{' '}
							<a
								className='forecast-pricing--link'
								onClick={(event) => {
									event.preventDefault();
									navigate(`${project?.editorBasePath}/costs`);
								}}
							>
								costs menu
							</a>
							.
						</p>
						{hasFirstProjectDiscount && (
							<p className='forecast-pricing--disclaimer'>
								<u>First project discount:</u> One month discount on your first
								project for the near-zero traffic forecast.
								<br />
								If your project has higher traffic on its first month, you only
								pay for the difference.
							</p>
						)}
						{!hasAtLeastOnePaymentMethod && (
							<p className='forecast-pricing--error'>
								Payment details required to publish. You will not be charged
								now.
								<br />
								You are charged on the first day of the month based on your
								balance.
							</p>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default PublishingPriceForecast;
